import { useEffect, useMemo, useState } from "react";
import imageCompression from "browser-image-compression";
import { getSupabase } from "../lib/supabase";
import { uploadInsightImage } from "../utils/uploadInsightImage";
import { SERVICE_OPTIONS } from "../config/constants";

const CATEGORY_OPTIONS = [
  "Tax",
  "Bookkeeping",
  "Payroll",
  "Financial Advisory",
  "Business Advisory",
  "Compliance",
  "Audit",
  "CFO",
  "Accounting",
];

const COUNTRY_OPTIONS = ["Global", "Pakistan", "UAE", "UK", "USA"];


const EMPTY_FORM = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Tax",
  country_target: "Global",
  service_target: "Tax Services",
  seo_title: "",
  seo_description: "",
  focus_keyword: "",
  secondary_keywords: "",
  reading_time: "",
  author_name: "AN NOOR Financial Advisors",
  meta_robots: "index, follow",
  is_published: false,
};

function slugify(value = "") {
  return value
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90);
}

function estimateReadingTime(content = "") {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
}

function parseKeywords(value = "") {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function keywordsToString(value) {
  if (!Array.isArray(value)) return "";
  return value.join(", ");
}

function validateForPublish(form) {
  const required = [
    ["title", "Title"],
    ["slug", "Slug"],
    ["excerpt", "Excerpt"],
    ["content", "Content"],
    ["category", "Category"],
    ["country_target", "Country target"],
    ["service_target", "Service target"],
    ["seo_title", "SEO title"],
    ["seo_description", "SEO description"],
    ["focus_keyword", "Focus keyword"],
  ];

  for (const [key, label] of required) {
    if (!String(form[key] || "").trim()) {
      return `${label} is required before publishing.`;
    }
  }

  if (form.slug !== slugify(form.slug)) {
    return "Slug must be lowercase, URL-safe, and should not contain spaces or special characters.";
  }

  if (form.seo_title.length > 70) {
    return "SEO title is too long. Keep it around 50–60 characters if possible.";
  }

  if (form.seo_description.length > 170) {
    return "SEO description is too long. Keep it around 140–160 characters if possible.";
  }

  if (form.excerpt.length < 80) {
    return "Excerpt is too short. Add a stronger summary for cards and SEO.";
  }

  if (form.content.length < 300) {
    return "Content is too short for an SEO article. Add more useful detail before publishing.";
  }

  return "";
}

async function compressImageToWebp(file) {
  if (!file) return null;

  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Please upload JPG, PNG, or WEBP image only.");
  }

  const compressedBlob = await imageCompression(file, {
    maxSizeMB: 0.35,
    maxWidthOrHeight: 1400,
    useWebWorker: true,
    fileType: "image/webp",
    initialQuality: 0.82,
  });

  const cleanName = file.name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .toLowerCase();

  return new File([compressedBlob], `${cleanName || "insight-image"}.webp`, {
    type: "image/webp",
    lastModified: Date.now(),
  });
}

function buildImagePreviewUrl(imageUrl, supabase) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;

  return supabase.storage.from("insights").getPublicUrl(imageUrl).data.publicUrl;
}

export default function InsightsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const [submitting, setSubmitting] = useState(false);

  const publishedCount = useMemo(
    () => items.filter((item) => item.is_published).length,
    [items]
  );

  async function fetchUpdates() {
    setLoading(true);
    setError("");

    const supabase = await getSupabase();

    const { data, error } = await supabase
      .schema("api")
      .from("updates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setError("Failed to load insights.");
      setItems([]);
    } else {
      setItems(data || []);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchUpdates();
  }, []);

  function updateFormField(key, value) {
    setForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "title" && !current.slug) {
        next.slug = slugify(value);
      }

      if (key === "content") {
        next.reading_time = estimateReadingTime(value);
      }

      return next;
    });
  }

  function updateEditField(key, value) {
    setEditForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "title" && !current.slug) {
        next.slug = slugify(value);
      }

      if (key === "content") {
        next.reading_time = estimateReadingTime(value);
      }

      return next;
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitting(true);

    try {
      const publishError = form.is_published ? validateForPublish(form) : "";

      if (publishError) {
        alert(publishError);
        setSubmitting(false);
        return;
      }

      const supabase = await getSupabase();
      let imageUrl = null;

      if (imageFile) {
        const webpFile = await compressImageToWebp(imageFile);
        imageUrl = await uploadInsightImage(webpFile);
      }

      const { error } = await supabase.schema("api").from("updates").insert({
        title: form.title.trim(),
        slug: form.slug.trim(),
        excerpt: form.excerpt.trim(),
        content: form.content.trim(),
        category: form.category,
        country_target: form.country_target,
        service_target: form.service_target,
        seo_title: form.seo_title.trim(),
        seo_description: form.seo_description.trim(),
        focus_keyword: form.focus_keyword.trim(),
        secondary_keywords: parseKeywords(form.secondary_keywords),
        reading_time: Number(form.reading_time || estimateReadingTime(form.content)),
        author_name: form.author_name || "AN NOOR Financial Advisors",
        meta_robots: form.meta_robots || "index, follow",
        image_url: imageUrl,
        is_published: Boolean(form.is_published),
        published_at: form.is_published ? new Date().toISOString() : null,
      });

      if (error) throw error;

      setForm(EMPTY_FORM);
      setImageFile(null);
      setImagePreview(null);
      await fetchUpdates();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to add insight.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdate() {
    if (!editing) return;

    setSubmitting(true);

    try {
      const publishError = editForm.is_published
        ? validateForPublish(editForm)
        : "";

      if (publishError) {
        alert(publishError);
        setSubmitting(false);
        return;
      }

      const supabase = await getSupabase();

      let imageUrl = editing.image_url;

      if (editImageFile) {
        const webpFile = await compressImageToWebp(editImageFile);
        imageUrl = await uploadInsightImage(webpFile);
      }

      const shouldSetPublishedAt =
        editForm.is_published && !editing.published_at;

      const { error } = await supabase
        .schema("api")
        .from("updates")
        .update({
          title: editForm.title.trim(),
          slug: editForm.slug.trim(),
          excerpt: editForm.excerpt.trim(),
          content: editForm.content.trim(),
          category: editForm.category,
          country_target: editForm.country_target,
          service_target: editForm.service_target,
          seo_title: editForm.seo_title.trim(),
          seo_description: editForm.seo_description.trim(),
          focus_keyword: editForm.focus_keyword.trim(),
          secondary_keywords: parseKeywords(editForm.secondary_keywords),
          reading_time: Number(
            editForm.reading_time || estimateReadingTime(editForm.content)
          ),
          author_name: editForm.author_name || "AN NOOR Financial Advisors",
          meta_robots: editForm.meta_robots || "index, follow",
          image_url: imageUrl,
          is_published: Boolean(editForm.is_published),
          published_at: shouldSetPublishedAt
            ? new Date().toISOString()
            : editing.published_at,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editing.id);

      if (error) throw error;

      setEditing(null);
      setEditImageFile(null);
      setEditImagePreview(null);
      await fetchUpdates();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update insight.");
    } finally {
      setSubmitting(false);
    }
  }

  async function togglePublish(item) {
    if (!item.is_published) {
      const tempForm = {
        ...item,
        secondary_keywords: keywordsToString(item.secondary_keywords),
      };

      const publishError = validateForPublish(tempForm);

      if (publishError) {
        alert(`Cannot publish yet: ${publishError}`);
        return;
      }
    }

    const supabase = await getSupabase();

    const { error } = await supabase
      .schema("api")
      .from("updates")
      .update({
        is_published: !item.is_published,
        published_at:
          !item.is_published && !item.published_at
            ? new Date().toISOString()
            : item.published_at,
        updated_at: new Date().toISOString(),
      })
      .eq("id", item.id);

    if (error) {
      console.error(error);
      alert("Failed to update publish status.");
      return;
    }

    await fetchUpdates();
  }

  async function deleteUpdate(id) {
    if (!confirm("Delete this insight permanently?")) return;

    const supabase = await getSupabase();

    const { error } = await supabase
      .schema("api")
      .from("updates")
      .delete()
      .eq("id", id);

    if (error) {
      console.error(error);
      alert("Failed to delete insight.");
      return;
    }

    await fetchUpdates();
  }

  async function startEditing(item) {
    const supabase = await getSupabase();

    setEditing(item);
    setEditForm({
      title: item.title || "",
      slug: item.slug || "",
      excerpt: item.excerpt || "",
      content: item.content || "",
      category: item.category || "Tax",
      country_target: item.country_target || "Global",
      service_target: item.service_target || "Tax Services",
      seo_title: item.seo_title || "",
      seo_description: item.seo_description || "",
      focus_keyword: item.focus_keyword || "",
      secondary_keywords: keywordsToString(item.secondary_keywords),
      reading_time: item.reading_time || estimateReadingTime(item.content || ""),
      author_name: item.author_name || "AN NOOR Financial Advisors",
      meta_robots: item.meta_robots || "index, follow",
      is_published: Boolean(item.is_published),
    });

    setEditImageFile(null);
    setEditImagePreview(buildImagePreviewUrl(item.image_url, supabase));
  }

  function handleCreateImageChange(file) {
    setImageFile(file || null);

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  }

  function handleEditImageChange(file) {
    setEditImageFile(file || null);

    if (file) {
      setEditImagePreview(URL.createObjectURL(file));
    } else if (editing?.image_url) {
      getSupabase().then((supabase) => {
        setEditImagePreview(buildImagePreviewUrl(editing.image_url, supabase));
      });
    } else {
      setEditImagePreview(null);
    }
  }

  return (
    <div className="space-y-12">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Insights</h1>
        <p className="mt-1 text-sm text-slate-500">
          Create SEO-ready insight articles with article URLs, metadata, and
          compressed WebP images.
        </p>

        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
            Total: {items.length}
          </span>
          <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">
            Published: {publishedCount}
          </span>
          <span className="rounded-full bg-yellow-100 px-3 py-1 text-yellow-700">
            Drafts: {items.length - publishedCount}
          </span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-xl backdrop-blur-xl dark:bg-white/5">
        <h2 className="mb-4 text-lg font-semibold">Add New Insight</h2>

        <form onSubmit={handleCreate} className="grid gap-5">
          <ArticleFormFields
            form={form}
            setField={updateFormField}
            imagePreview={imagePreview}
            onImageChange={handleCreateImageChange}
          />

          <button
            disabled={submitting}
            className="self-start rounded-xl bg-[rgb(var(--brand-primary))] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? "Adding…" : "Add Insight"}
          </button>
        </form>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonInsight key={i} />
          ))}
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((u) => (
            <div
              key={u.id}
              className="rounded-2xl border border-white/20 bg-white/70 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5"
            >
              <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                <div className="max-w-4xl">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {u.category && (
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                        {u.category}
                      </span>
                    )}

                    {u.country_target && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {u.country_target}
                      </span>
                    )}

                    {u.slug ? (
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                        Slug OK
                      </span>
                    ) : (
                      <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                        Missing slug
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold">{u.title}</h3>

                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">
                    {u.excerpt || u.content}
                  </p>

                  {u.slug && (
                    <p className="mt-2 text-xs text-slate-500">
                      /insights/{u.slug}
                    </p>
                  )}
                </div>

                <span
                  className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                    u.is_published
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {u.is_published ? "Published" : "Draft"}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  onClick={() => startEditing(u)}
                  className="rounded-lg bg-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-700"
                >
                  Edit
                </button>

                <button
                  onClick={() => togglePublish(u)}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                    u.is_published
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-slate-400 text-white hover:bg-slate-500"
                  }`}
                >
                  {u.is_published ? "Unpublish" : "Publish"}
                </button>

                <button
                  onClick={() => deleteUpdate(u.id)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-lg font-semibold">Edit Insight</h2>

            <div className="grid gap-5">
              <ArticleFormFields
                form={editForm}
                setField={updateEditField}
                imagePreview={editImagePreview}
                onImageChange={handleEditImageChange}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditing(null);
                  setEditImageFile(null);
                  setEditImagePreview(null);
                }}
                className="rounded border px-4 py-2 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdate}
                disabled={submitting}
                className="rounded bg-[rgb(var(--brand-primary))] px-4 py-2 text-sm text-white disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ArticleFormFields({ form, setField, imagePreview, onImageChange }) {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Title">
          <input
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            required
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Article title"
          />
        </Field>

        <Field label="Slug">
          <input
            value={form.slug}
            onChange={(e) => setField("slug", slugify(e.target.value))}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="seo-friendly-url"
          />
        </Field>
      </div>

      <Field label="Excerpt">
        <textarea
          value={form.excerpt}
          onChange={(e) => setField("excerpt", e.target.value)}
          rows={3}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Short summary for cards and SEO context"
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Category">
          <select
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {CATEGORY_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>

        <Field label="Country Target">
          <select
            value={form.country_target}
            onChange={(e) => setField("country_target", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {COUNTRY_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>

        <Field label="Service Target">
          <select
            value={form.service_target}
            onChange={(e) => setField("service_target", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            {SERVICE_OPTIONS.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="SEO Title">
          <input
            value={form.seo_title}
            onChange={(e) => setField("seo_title", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Recommended: 50–60 characters"
          />
          <p className="mt-1 text-xs text-slate-400">
            {form.seo_title.length} characters
          </p>
        </Field>

        <Field label="Focus Keyword">
          <input
            value={form.focus_keyword}
            onChange={(e) => setField("focus_keyword", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="Main keyword"
          />
        </Field>
      </div>

      <Field label="SEO Description">
        <textarea
          value={form.seo_description}
          onChange={(e) => setField("seo_description", e.target.value)}
          rows={3}
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Recommended: 140–160 characters"
        />
        <p className="mt-1 text-xs text-slate-400">
          {form.seo_description.length} characters
        </p>
      </Field>

      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Secondary Keywords">
          <input
            value={form.secondary_keywords}
            onChange={(e) => setField("secondary_keywords", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="keyword one, keyword two"
          />
        </Field>

        <Field label="Reading Time">
          <input
            type="number"
            min="1"
            value={form.reading_time}
            onChange={(e) => setField("reading_time", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Robots">
          <select
            value={form.meta_robots}
            onChange={(e) => setField("meta_robots", e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          >
            <option>index, follow</option>
            <option>noindex, follow</option>
            <option>noindex, nofollow</option>
          </select>
        </Field>
      </div>

      <Field label="Featured Image">
        {imagePreview && (
          <div className="mb-3 overflow-hidden rounded-xl border">
            <img
              src={imagePreview}
              alt="Preview"
              loading="lazy"
              decoding="async"
              width="900"
              height="450"
              className="h-56 w-full object-cover"
            />
          </div>
        )}

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => onImageChange(e.target.files?.[0])}
          className="w-full rounded-lg border px-3 py-2 text-sm"
        />

        <p className="mt-1 text-xs text-slate-400">
          Images are automatically compressed and converted to WebP before
          upload.
        </p>
      </Field>

      <Field label="Content">
        <textarea
          value={form.content}
          onChange={(e) => setField("content", e.target.value)}
          rows={10}
          required
          className="w-full rounded-lg border px-3 py-2 text-sm"
          placeholder="Full article content"
        />
      </Field>

      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={form.is_published}
          onChange={(e) => setField("is_published", e.target.checked)}
        />
        Publish immediately
      </label>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold tracking-wide text-slate-500 uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}

function SkeletonInsight() {
  return (
    <div className="animate-pulse rounded-2xl border border-white/20 bg-white/60 p-6 shadow-lg backdrop-blur-xl dark:bg-white/5">
      <div className="mb-3 h-5 w-1/3 rounded bg-slate-300/60" />
      <div className="mb-2 h-4 w-full rounded bg-slate-300/40" />
      <div className="mb-2 h-4 w-5/6 rounded bg-slate-300/40" />
      <div className="h-4 w-2/3 rounded bg-slate-300/40" />
    </div>
  );
}